import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Savings as SavingsIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { SavingsGoal } from '../types';
import { formatCurrency } from '../utils/dateUtils';

interface SavingsGoalsProps {
  goals: SavingsGoal[];
  onAddGoal: (goal: Omit<SavingsGoal, 'id' | 'currentAmount'>) => void;
  onEditGoal: (goal: SavingsGoal) => void;
  onDeleteGoal: (goalId: string) => void;
  onContribute: (goalId: string, amount: number) => void;
}

const SavingsGoals: React.FC<SavingsGoalsProps> = ({
  goals,
  onAddGoal,
  onEditGoal,
  onDeleteGoal,
  onContribute,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contributeDialogOpen, setContributeDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<SavingsGoal | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<SavingsGoal | null>(null);
  const [contributionAmount, setContributionAmount] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    targetAmount: '',
    targetDate: '',
    description: '',
    category: 'general' as 'emergency' | 'vacation' | 'purchase' | 'general',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      targetAmount: '',
      targetDate: '',
      description: '',
      category: 'general',
    });
    setEditingGoal(null);
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.targetAmount || !formData.targetDate) {
      return;
    }

    const getCategoryColor = (category: string) => {
      const colors = {
        emergency: '#f44336',
        vacation: '#2196f3',
        purchase: '#ff9800',
        general: '#4caf50',
      };
      return colors[category as keyof typeof colors] || '#4caf50';
    };

    const goalData = {
      ...formData,
      targetAmount: parseFloat(formData.targetAmount),
      targetDate: new Date(formData.targetDate),
      color: getCategoryColor(formData.category),
    };

    if (editingGoal) {
      onEditGoal({
        ...editingGoal,
        ...goalData,
      });
    } else {
      onAddGoal(goalData);
    }

    setDialogOpen(false);
    resetForm();
  };

  const handleEdit = (goal: SavingsGoal) => {
    setEditingGoal(goal);
    setFormData({
      name: goal.name,
      targetAmount: goal.targetAmount.toString(),
      targetDate: goal.targetDate.toISOString().split('T')[0],
      description: goal.description || '',
      category: goal.category,
    });
    setDialogOpen(true);
  };

  const handleContribute = () => {
    if (!selectedGoal || !contributionAmount) return;
    
    const amount = parseFloat(contributionAmount);
    if (amount > 0) {
      onContribute(selectedGoal.id, amount);
      setContributeDialogOpen(false);
      setContributionAmount('');
      setSelectedGoal(null);
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return 'success';
    if (progress >= 75) return 'info';
    if (progress >= 50) return 'warning';
    return 'error';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'emergency': return '🚨';
      case 'vacation': return '✈️';
      case 'purchase': return '🛍️';
      default: return '🎯';
    }
  };

  const getCategoryName = (category: string) => {
    switch (category) {
      case 'emergency': return 'Urgence';
      case 'vacation': return 'Vacances';
      case 'purchase': return 'Achat';
      default: return 'Général';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <SavingsIcon sx={{ mr: 2 }} />
          Objectifs d'épargne
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Nouvel objectif
        </Button>
      </Box>

      {goals.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Aucun objectif d'épargne</Typography>
          <Typography>
            Commencez par définir vos objectifs financiers pour mieux organiser votre épargne !
          </Typography>
        </Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {goals.map((goal) => {
            const progress = (goal.currentAmount / goal.targetAmount) * 100;
            const isCompleted = progress >= 100;
            const daysRemaining = Math.ceil((goal.targetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
            
            return (
              <Box key={goal.id}>
                <Card sx={{ height: '100%', position: 'relative' }}>
                  {isCompleted && (
                    <CheckCircleIcon 
                      sx={{ 
                        position: 'absolute', 
                        top: 16, 
                        right: 16, 
                        color: 'success.main',
                        fontSize: 32,
                        zIndex: 1,
                      }} 
                    />
                  )}
                  
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h4" sx={{ mr: 1 }}>
                        {getCategoryIcon(goal.category)}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" noWrap>
                          {goal.name}
                        </Typography>
                        <Chip
                          label={getCategoryName(goal.category)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Progression
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {progress.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        color={getProgressColor(progress)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(goal.currentAmount)} / {formatCurrency(goal.targetAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Reste: {formatCurrency(goal.targetAmount - goal.currentAmount)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color={daysRemaining > 0 ? 'text.secondary' : 'error'}>
                        {daysRemaining > 0 
                          ? `${daysRemaining} jours restants`
                          : daysRemaining === 0 
                          ? 'Échéance aujourd\'hui'
                          : `En retard de ${Math.abs(daysRemaining)} jours`
                        }
                      </Typography>
                    </Box>

                    {goal.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {goal.description}
                      </Typography>
                    )}
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<TrendingUpIcon />}
                      onClick={() => {
                        setSelectedGoal(goal);
                        setContributeDialogOpen(true);
                      }}
                      disabled={isCompleted}
                      sx={{ flex: 1 }}
                    >
                      Contribuer
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(goal)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDeleteGoal(goal.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Dialog Ajout/Édition */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGoal ? 'Modifier l\'objectif' : 'Nouvel objectif d\'épargne'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nom de l'objectif"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Montant cible"
              type="number"
              value={formData.targetAmount}
              onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Date cible"
              type="date"
              value={formData.targetDate}
              onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              select
              label="Catégorie"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="general">🎯 Général</option>
              <option value="emergency">🚨 Urgence</option>
              <option value="vacation">✈️ Vacances</option>
              <option value="purchase">🛍️ Achat</option>
            </TextField>
            
            <TextField
              fullWidth
              label="Description (optionnel)"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingGoal ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Contribution */}
      <Dialog open={contributeDialogOpen} onClose={() => setContributeDialogOpen(false)}>
        <DialogTitle>Contribuer à l'objectif</DialogTitle>
        <DialogContent>
          {selectedGoal && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedGoal.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progression actuelle: {formatCurrency(selectedGoal.currentAmount)} / {formatCurrency(selectedGoal.targetAmount)}
              </Typography>
              <TextField
                fullWidth
                label="Montant de la contribution"
                type="number"
                value={contributionAmount}
                onChange={(e) => setContributionAmount(e.target.value)}
                autoFocus
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContributeDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleContribute} variant="contained">
            Contribuer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SavingsGoals;
